import { type FC, useRef, useState } from "react";

import { ArrowRight, Calendar, Check, Phone } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { CalendlyModal } from "@/shared/components/overlay/CalendlyModal";
import { Button } from "@/shared/components/ui/button";
import { CALENDLY_URLS, ESTATE_EXPERT_BOOKING_SOURCE } from "@/shared/constants/calendlyUrls";
import { PLAN_NAME_BY_TIER } from "@/shared/constants/planTiers";
import { useAttorneyHelplineEnabled } from "@/shared/hooks/useAttorneyHelplineEnabled";
import { usePerson, useUser } from "@/shared/hooks/useAppContext";
import { useCalendlyBooking } from "@/shared/hooks/useCalendlyBooking";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { useSaveCalendlyBooking } from "@/shared/hooks/useSaveCalendlyBooking";
import { formatDate, formatTime, getFullName } from "@/shared/utils/formatters";
import { notifyBookingFlowOpened } from "@/shared/utils/slackNotifications";
import { hasMinTier } from "@/shared/utils/tierOrder";

interface EstateExpertWidgetProps {
  onSeePlans?: () => void;
}

const BODY_COPY = "A call with an Estate Expert who can already see what you've shared.";

export const EstateExpertWidget: FC<EstateExpertWidgetProps> = ({ onSeePlans }) => {
  const helplineEnabled = useAttorneyHelplineEnabled();
  const person = usePerson();
  const user = useUser();
  const estateId = person?.estate_id;
  const { tier, attorneyHelplineMinTier } = usePaymentTier();
  const canBookCall = hasMinTier(tier, attorneyHelplineMinTier);
  const includedPlanName = PLAN_NAME_BY_TIER[attorneyHelplineMinTier];
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const slotSelectedRef = useRef(false);
  const { booking } = useCalendlyBooking(
    helplineEnabled ? estateId : undefined,
    ESTATE_EXPERT_BOOKING_SOURCE,
  );
  const { mutate: saveBooking } = useSaveCalendlyBooking(estateId, ESTATE_EXPERT_BOOKING_SOURCE);
  const userName = getFullName(user?.first_name ?? null, user?.last_name ?? null);
  const upcomingBooking =
    booking && (!booking.start_time || new Date(booking.start_time) > new Date()) ? booking : null;

  if (!helplineEnabled) return null;

  return (
    <div className="flex flex-col gap-6 border border-primary rounded-lg bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="size-8 rounded bg-blue-100 flex items-center justify-center shrink-0">
            <Phone className="size-4 text-blue-600" />
          </div>
          <p className="text-base font-semibold tracking-[-0.02em]">Talk to an Estate Expert</p>
        </div>
        <p className="text-sm text-secondary tracking-[-0.02em]">
          {upcomingBooking || canBookCall
            ? BODY_COPY
            : `${BODY_COPY} Included with ${includedPlanName}.`}
        </p>
      </div>
      <div className="flex flex-col items-center gap-3">
        {upcomingBooking ? (
          <>
            <div className="flex items-center justify-center gap-2.5 w-full rounded-full bg-brand-subtle px-6 py-2">
              <Check className="size-4 text-brand" />
              <span className="text-sm font-medium text-brand">Call booked</span>
            </div>
            {upcomingBooking.start_time && (
              <p className="text-xs text-secondary text-center tracking-normal">
                Your call is {formatDate(upcomingBooking.start_time)} at{" "}
                {formatTime(upcomingBooking.start_time)}
              </p>
            )}
          </>
        ) : canBookCall ? (
          <Button
            variant="brand"
            rounded
            className="w-full gap-2"
            onClick={() => setCalendlyOpen(true)}
          >
            <Calendar className="size-4" />
            Book a call
          </Button>
        ) : (
          <Button variant="outline" rounded className="w-full gap-2" onClick={onSeePlans}>
            See plans
            <ArrowRight className="size-4" />
          </Button>
        )}
      </div>
      <CalendlyModal
        isOpen={calendlyOpen}
        onClose={() => {
          setCalendlyOpen(false);
          slotSelectedRef.current = false;
        }}
        url={CALENDLY_URLS.ESTATE_SETTLEMENT_QUOTE}
        prefill={{ name: userName || undefined, email: user?.email ?? undefined }}
        onDateAndTimeSelected={() => {
          if (slotSelectedRef.current) return;
          slotSelectedRef.current = true;
          notifyBookingFlowOpened();
        }}
        onEventScheduled={(eventUri, inviteeUri) =>
          saveBooking(
            { eventUri, inviteeUri },
            {
              onError: () =>
                enqueueSnackbar(
                  "Your call is booked, but we couldn't save it here. Calendly will email you the details.",
                  { variant: "error" },
                ),
            },
          )
        }
      />
    </div>
  );
};
